import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Chip,
    useTheme,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    IconButton,
    InputAdornment,
    Tooltip
} from '@mui/material';
import { format, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import AddIcon from '@mui/icons-material/Add';
import MicIcon from '@mui/icons-material/Mic';
import { Obra, Tarea } from '../../types';

interface AddTaskDialogProps {
    open: boolean;
    onClose: () => void;
    onAdd: (nombre: string, fecha: Date, obraId: string, recurrente: boolean) => Promise<void>;
    daysOfWeek: Date[];
    obras: Obra[];
    taskToEdit?: Tarea | null;
    onUpdate?: (id: string, data: Partial<Tarea>) => Promise<void>;
}

// Interface for Web Speech API
interface IWindow extends Window {
    webkitSpeechRecognition: any;
    SpeechRecognition: any;
}

const AddTaskDialog: React.FC<AddTaskDialogProps> = ({
    open,
    onClose,
    onAdd,
    daysOfWeek,
    obras,
    taskToEdit,
    onUpdate
}) => {
    const [nombre, setNombre] = useState('');
    const [selectedObraId, setSelectedObraId] = useState('');
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [isRecurrent, setIsRecurrent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef<any>(null);
    const theme = useTheme();

    // Reset or populate state when opening
    useEffect(() => {
        if (open) {
            if (taskToEdit) {
                setNombre(taskToEdit.nombre);
                setSelectedObraId(taskToEdit.obraId);
                setSelectedDate(taskToEdit.fechaProgramada ? new Date(taskToEdit.fechaProgramada) : new Date());
                setIsRecurrent(taskToEdit.recurrente || false);
            } else {
                setNombre('');
                setSelectedObraId('');
                setSelectedDate(new Date());
                setIsRecurrent(false);
            }
        }
    }, [open, taskToEdit]);

    useEffect(() => {
        if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
            const SpeechRecognition = (window as unknown as IWindow).SpeechRecognition || (window as unknown as IWindow).webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.lang = 'es-ES';

            recognitionRef.current.onresult = (event: any) => {
                let finalTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    if (event.results[i].isFinal) {
                        finalTranscript += event.results[i][0].transcript;
                    }
                }
                if (finalTranscript) {
                    setNombre(prev => {
                        const newText = prev ? `${prev} ${finalTranscript}` : finalTranscript;
                        // Clean up double spaces just in case
                        return newText.replace(/\s+/g, ' ').trim();
                    });
                }
            };

            recognitionRef.current.onerror = (event: any) => {
                console.error('Speech recognition error', event.error);
                setIsListening(false);
            };

            recognitionRef.current.onend = () => {
                setIsListening(false);
            };
        }
    }, []);

    const startListening = () => {
        if (recognitionRef.current) {
            setIsListening(true);
            try {
                recognitionRef.current.start();
            } catch (error) {
                console.error("Error starting recognition:", error);
            }
        } else {
            alert('Tu navegador no soporta reconocimiento de voz.');
        }
    };

    const stopListening = () => {
        if (recognitionRef.current) {
            setIsListening(false);
            recognitionRef.current.stop();
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!nombre.trim() || !selectedObraId) return;

        try {
            setLoading(true);
            const dateToSave = new Date(selectedDate);
            dateToSave.setHours(12, 0, 0, 0);

            const targetObra = obras.find(o => o.id === selectedObraId);

            if (taskToEdit && onUpdate) {
                await onUpdate(taskToEdit.id, {
                    nombre,
                    fechaProgramada: dateToSave,
                    obraId: selectedObraId,
                    obraNombre: targetObra?.nombre,
                    recurrente: isRecurrent
                });
            } else {
                await onAdd(nombre, dateToSave, selectedObraId, isRecurrent);
            }

            // Cleanup handled by parent closing or useEffect
            onClose();
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: { borderRadius: 3 }
            }}
        >
            <DialogTitle sx={{ fontWeight: 'bold' }}>
                {taskToEdit ? 'Editar Tarea' : 'Nueva Tarea'}
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="obra-select-label">Obra</InputLabel>
                        <Select
                            labelId="obra-select-label"
                            id="obra-select"
                            value={selectedObraId}
                            label="Obra"
                            onChange={(e) => setSelectedObraId(e.target.value)}
                            disabled={loading}
                        >
                            {obras.map((obra) => (
                                <MenuItem key={obra.id} value={obra.id}>
                                    {obra.nombre}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        autoFocus
                        label="Descripción de la tarea"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={nombre}
                        onChange={(e) => setNombre(e.target.value)}
                        disabled={loading}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onMouseDown={startListening}
                                        onMouseUp={stopListening}
                                        onTouchStart={startListening}
                                        onTouchEnd={stopListening}
                                        onContextMenu={(e) => e.preventDefault()}
                                        color={isListening ? "error" : "primary"}
                                        edge="end"
                                    >
                                        <MicIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={isRecurrent}
                                onChange={(e) => setIsRecurrent(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Repetir semanalmente (Recurrente)"
                    />

                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Programar para:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {daysOfWeek.map((day) => {
                            const isSelected = isSameDay(day, selectedDate);
                            return (
                                <Chip
                                    key={day.toISOString()}
                                    label={format(day, 'EEE d', { locale: es })}
                                    onClick={() => setSelectedDate(day)}
                                    color={isSelected ? 'primary' : 'default'}
                                    variant={isSelected ? 'filled' : 'outlined'}
                                    sx={{
                                        textTransform: 'capitalize',
                                        fontWeight: isSelected ? 'bold' : 'medium',
                                        '&:hover': {
                                            bgcolor: isSelected ? 'primary.dark' : 'action.hover'
                                        }
                                    }}
                                />
                            );
                        })}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={onClose} disabled={loading} color="inherit">
                        Cancelar
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        startIcon={<AddIcon />}
                        disabled={!nombre.trim() || !selectedObraId || loading}
                        sx={{ borderRadius: 2 }}
                    >
                        {taskToEdit ? 'Guardar Cambios' : 'Agregar Tarea'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddTaskDialog;
